import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Mountain, Heart, TreePine, Clock, Loader2, ShoppingCart, Clapperboard, Flame, Users, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { IMAGES } from "@/lib/constants";
import { useCart } from "@/context/CartContext";
import { useExperiences } from "@/hooks/useExperiences";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const categoryIcons = {
  adventure: Mountain,
  romantic: Heart,
  nature: TreePine,
  entertainment: Clapperboard,
};

const PriceDisplay = ({ exp }) => {
  const mode = exp.pricing_mode || "per_group";
  const max = Number(exp.max_guests) || 0;
  if (mode === "per_person") {
    return (
      <div className="mb-4">
        <span className="font-heading text-2xl font-semibold text-primary">${exp.price.toFixed(2)}</span>
        <span className="font-body text-xs text-muted-foreground ml-2">per person{max ? ` · up to ${max} guests` : ""}</span>
        {Number(exp.child_price) > 0 && (
          <p className="font-body text-xs text-accent mt-0.5">kids ${Number(exp.child_price).toFixed(2)} each</p>
        )}
      </div>
    );
  }
  if (mode === "both") {
    return (
      <div className="mb-4">
        <div>
          <span className="font-heading text-2xl font-semibold text-primary">${exp.price.toFixed(2)}</span>
          <span className="font-body text-xs text-muted-foreground ml-2">per person{Number(exp.child_price) > 0 ? ` · kids $${Number(exp.child_price).toFixed(2)}` : ""}</span>
        </div>
        <p className="font-body text-xs text-accent mt-0.5">
          or ${Number(exp.group_price || exp.price).toFixed(2)} group rate{max ? ` (up to ${max} guests)` : ""}
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between mb-4">
      <span className="font-heading text-2xl font-semibold text-primary">${exp.price.toFixed(2)}</span>
      <span className="font-body text-xs text-muted-foreground">
        {max ? `group rate · up to ${max} guests` : ["entertainment", "romantic"].includes(exp.category) ? "per setup" : "per group"}
      </span>
    </div>
  );
};

const ExperienceCard = ({ exp, onBook, onRequest, onAddToCart, loading }) => {
  const Icon = categoryIcons[exp.category] || TreePine;
  return (
    <Card className="overflow-hidden border border-border/50 card-lift h-full flex flex-col">
      <div className="img-hover-zoom aspect-[16/10]">
        <img src={exp.image} alt={exp.name} className="w-full h-full object-cover" />
      </div>
      <CardContent className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Icon size={16} className="text-accent" />
          <span className="font-body text-xs text-muted-foreground uppercase tracking-wider">{exp.category}</span>
        </div>
        <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{exp.name}</h3>
        <p className="font-body text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{exp.description}</p>
        <div className="flex items-center gap-2 mb-4">
          <Clock size={14} className="text-muted-foreground" />
          <span className="font-body text-xs text-muted-foreground">{exp.duration}</span>
        </div>
        <PriceDisplay exp={exp} />
        <div className="space-y-2 mt-auto">
          <Button
            data-testid={`add-to-cart-${exp.id}`}
            onClick={() => onAddToCart(exp)}
            className="w-full bg-accent hover:bg-accent/90 text-white font-body text-sm rounded-full"
          >
            <ShoppingCart size={15} className="mr-2" /> Add to Cart
          </Button>
          <div className="flex gap-2">
            <Button
              data-testid={`book-${exp.id}`}
              onClick={() => onBook(exp.id)}
              disabled={loading === exp.id}
              variant="outline"
              className="flex-1 font-body text-sm rounded-full border-primary text-primary hover:bg-primary hover:text-white"
            >
              {loading === exp.id ? <Loader2 size={16} className="animate-spin" /> : "Book & Pay Now"}
            </Button>
            <Button
              data-testid={`request-${exp.id}`}
              onClick={() => onRequest(exp)}
              variant="outline"
              className="font-body text-sm rounded-full"
            >
              Request
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RequestDialog = ({ open, onOpenChange, exp, form, setForm, loading, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md" data-testid="experience-request-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">
          Request: {exp?.name}
        </DialogTitle>
        <DialogDescription className="font-body text-xs">
          Tell us when you'd like this experience and we'll coordinate everything.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label className="font-body text-sm">Name *</Label>
          <Input
            data-testid="request-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="font-body"
          />
        </div>
        <div>
          <Label className="font-body text-sm">Email *</Label>
          <Input
            data-testid="request-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="font-body"
          />
        </div>
        <div>
          <Label className="font-body text-sm">Phone</Label>
          <Input
            data-testid="request-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="font-body"
          />
        </div>
        <div>
          <Label className="font-body text-sm">Preferred Date</Label>
          <Input
            data-testid="request-date"
            type="date"
            value={form.preferred_date}
            onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
            className="font-body"
          />
        </div>
        <div>
          <Label className="font-body text-sm">Message</Label>
          <Textarea
            data-testid="request-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="font-body"
            rows={3}
          />
        </div>
        <Button
          type="submit"
          data-testid="request-submit"
          disabled={loading}
          className="w-full bg-primary hover:bg-primary/90 text-white font-body rounded-full"
        >
          {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
          Submit Request
        </Button>
      </form>
    </DialogContent>
  </Dialog>
);

const TheaterDialog = ({ open, onOpenChange, exp, onConfirm }) => {
  const [date, setDate] = useState("");
  const [film, setFilm] = useState("");

  useEffect(() => {
    if (open) {
      setDate("");
      setFilm("");
    }
  }, [open]);

  const confirm = () => {
    if (!date) {
      toast.error("Please pick your movie-night date");
      return;
    }
    onConfirm({ event_date: date, film: film.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="theater-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Clapperboard size={18} className="text-accent" /> {exp?.name}
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            Pick your movie-night date so we can have everything ready.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="font-body text-sm">Movie Night Date *</Label>
            <Input
              data-testid="theater-date"
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="font-body"
            />
          </div>
          <div>
            <Label className="font-body text-sm">Preferred Film (optional)</Label>
            <Input
              data-testid="theater-film"
              placeholder="e.g., The Notebook"
              value={film}
              onChange={(e) => setFilm(e.target.value)}
              className="font-body"
            />
            <p className="font-body text-xs text-muted-foreground mt-1">We'll do our best to arrange your pick.</p>
          </div>
          <Button data-testid="theater-add-to-cart" onClick={confirm} className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full">
            <ShoppingCart size={15} className="mr-2" /> Add to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PricingDialog = ({ open, onOpenChange, exp, onConfirm }) => {
  const mode = exp?.pricing_mode || "per_group";
  const max = Number(exp?.max_guests) || 10;
  const [option, setOption] = useState("per_person");
  const [people, setPeople] = useState(1);
  const [kids, setKids] = useState(0);
  const [date, setDate] = useState("");

  useEffect(() => {
    if (open) {
      setOption("per_person");
      setPeople(1);
      setKids(0);
      setDate("");
    }
  }, [open]);

  if (!exp) return null;
  const groupPrice = Number(exp.group_price) || exp.price;
  const childPrice = Number(exp.child_price) || 0;
  const effectiveOption = mode === "per_group" ? "per_group" : mode === "per_person" ? "per_person" : option;
  const total = effectiveOption === "per_person" ? exp.price * people + childPrice * kids : mode === "per_group" ? exp.price : groupPrice;

  const confirm = () => {
    onConfirm({
      pricing_option: effectiveOption,
      people: effectiveOption === "per_person" ? people : 1,
      children: effectiveOption === "per_person" ? kids : 0,
      event_date: date,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="pricing-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Users size={18} className="text-accent" /> {exp.name}
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            {mode === "both" ? "Choose how you'd like to book this experience." : mode === "per_person" ? "How many guests will join?" : "Pick a preferred date and we'll have everything ready."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {mode === "both" && (
            <div className="space-y-2">
              <button
                data-testid="pricing-option-per-person"
                onClick={() => setOption("per_person")}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${option === "per_person" ? "border-accent bg-accent/5" : "border-border/60 hover:border-border"}`}
              >
                <p className="font-body text-sm font-semibold text-foreground">Per person — ${exp.price.toFixed(2)} each</p>
                <p className="font-body text-xs text-muted-foreground">Pay for exactly the guests joining</p>
              </button>
              <button
                data-testid="pricing-option-per-group"
                onClick={() => setOption("per_group")}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${option === "per_group" ? "border-accent bg-accent/5" : "border-border/60 hover:border-border"}`}
              >
                <p className="font-body text-sm font-semibold text-foreground">Group rate — ${groupPrice.toFixed(2)} flat</p>
                <p className="font-body text-xs text-muted-foreground">One price for groups of up to {max} guests</p>
              </button>
            </div>
          )}
          {effectiveOption === "per_person" && (
            <div className={childPrice > 0 ? "grid grid-cols-2 gap-3" : ""}>
              <div>
                <Label className="font-body text-sm">{childPrice > 0 ? "Adults" : "Number of Guests"}</Label>
                <div className="flex items-center gap-3 mt-1">
                  <button data-testid="pricing-people-minus" onClick={() => setPeople((p) => Math.max(1, p - 1))} className="h-9 w-9 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary" aria-label="Fewer guests">
                    <Minus size={14} />
                  </button>
                  <span data-testid="pricing-people-count" className="font-heading text-lg font-semibold w-8 text-center">{people}</span>
                  <button data-testid="pricing-people-plus" onClick={() => setPeople((p) => Math.min(max - kids, p + 1))} className="h-9 w-9 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary" aria-label="More guests">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              {childPrice > 0 && (
                <div>
                  <Label className="font-body text-sm">Kids — ${childPrice.toFixed(2)} each</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <button data-testid="pricing-kids-minus" onClick={() => setKids((k) => Math.max(0, k - 1))} className="h-9 w-9 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary" aria-label="Fewer kids">
                      <Minus size={14} />
                    </button>
                    <span data-testid="pricing-kids-count" className="font-heading text-lg font-semibold w-8 text-center">{kids}</span>
                    <button data-testid="pricing-kids-plus" onClick={() => setKids((k) => Math.min(max - people, k + 1))} className="h-9 w-9 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary" aria-label="More kids">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              )}
              <p className={`font-body text-xs text-muted-foreground mt-1.5 ${childPrice > 0 ? "col-span-2" : ""}`}>
                Max {max} guests total{people + kids >= max ? " — contact us for larger groups" : ""}
              </p>
            </div>
          )}
          <div>
            <Label className="font-body text-sm">Preferred Date (optional)</Label>
            <Input
              data-testid="pricing-date"
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="font-body"
            />
            <p className="font-body text-xs text-muted-foreground mt-1">We'll schedule it for this day — leave blank to coordinate later.</p>
          </div>
          <div className="flex items-center justify-between border-t border-border/60 pt-3">
            <span className="font-body text-sm text-muted-foreground">Total</span>
            <span data-testid="pricing-total" className="font-heading text-xl font-semibold text-primary">${total.toFixed(2)}</span>
          </div>
          <Button data-testid="pricing-add-to-cart" onClick={confirm} className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full">
            <ShoppingCart size={15} className="mr-2" /> Add to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SpecialsSection = ({ onAdd }) => {
  const [specials, setSpecials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/specials`).then((r) => setSpecials(r.data || [])).catch(() => {});
  }, []);

  if (!specials.length) return null;

  const daysLeft = (d) => Math.max(0, Math.ceil((new Date(`${d}T23:59:59`) - new Date()) / 86400000));

  return (
    <motion.div {...fadeUp} data-testid="specials-section" className="mb-14">
      <div className="flex items-center gap-2 mb-5">
        <Flame size={18} className="text-accent" />
        <h2 className="font-heading text-2xl font-semibold text-foreground">Limited-Time Specials</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {specials.map((sp) => (
          <div key={sp.id} data-testid={`special-card-${sp.id}`} className="rounded-xl overflow-hidden border-2 border-accent/40 bg-white flex flex-col sm:flex-row card-lift">
            {sp.image && (
              <div className="sm:w-52 shrink-0">
                <img src={sp.image} alt={sp.name} className="w-full h-44 sm:h-full object-cover" />
              </div>
            )}
            <div className="p-5 flex flex-col flex-1">
              <span className="font-body text-[11px] uppercase tracking-wider text-accent font-semibold mb-1">
                Limited time · ends in {daysLeft(sp.valid_until)} days
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-1">{sp.name}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed flex-1 mb-3">{sp.description}</p>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="font-heading text-xl font-semibold text-primary">${Number(sp.price).toFixed(2)}</span>
                  {sp.regular_price > sp.price && (
                    <span className="font-body text-sm text-muted-foreground line-through ml-2">${Number(sp.regular_price).toFixed(2)}</span>
                  )}
                </div>
                <Button
                  data-testid={`special-add-${sp.id}`}
                  size="sm"
                  onClick={() => {
                    onAdd(sp);
                    toast.success(`${sp.name} added to cart`);
                  }}
                  className="bg-accent hover:bg-accent/90 text-white font-body text-xs rounded-full"
                >
                  <ShoppingCart size={13} className="mr-1" /> Add to Cart
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default function ExperiencesPage() {
  const [loading, setLoading] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", message: "" });
  const [formLoading, setFormLoading] = useState(false);
  const { addExperience } = useCart();
  const EXPERIENCES = useExperiences();
  const [theaterOpen, setTheaterOpen] = useState(false);
  const [theaterExp, setTheaterExp] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingExp, setPricingExp] = useState(null);

  const handleAddToCart = (exp) => {
    if (exp.category === "entertainment") {
      setTheaterExp(exp);
      setTheaterOpen(true);
      return;
    }
    setPricingExp(exp);
    setPricingOpen(true);
  };

  const handlePricingConfirm = (details) => {
    addExperience(pricingExp, details);
    const parts = [];
    if (details.pricing_option === "per_person") {
      parts.push(`${details.people} guest${details.people > 1 ? "s" : ""}${details.children ? ` + ${details.children} kid${details.children > 1 ? "s" : ""}` : ""}`);
    } else if ((pricingExp.pricing_mode || "per_group") === "both") {
      parts.push("Group rate");
    }
    if (details.event_date) parts.push(`on ${details.event_date}`);
    toast.success(`${pricingExp.name} added to cart`, {
      description: parts.join(" ") || "Open the cart in the top menu to check out.",
    });
  };

  const handleTheaterConfirm = (details) => {
    addExperience(theaterExp, details);
    toast.success(`${theaterExp.name} added to cart`, { description: `Movie night on ${details.event_date}` });
  };

  const handleBook = async (experienceId) => {
    setLoading(experienceId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/checkout/create-session`, {
        experience_id: experienceId,
        origin_url: originUrl,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(null);
    }
  };

  const handleRequest = (exp) => {
    setSelectedExp(exp);
    setRequestOpen(true);
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setFormLoading(true);
    try {
      await axios.post(`${API}/experience-requests`, {
        ...form,
        experience_ids: selectedExp ? [selectedExp.id] : [],
      });
      toast.success("Request submitted! We will contact you shortly.");
      setRequestOpen(false);
      setForm({ name: "", email: "", phone: "", preferred_date: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Experiences & Add-Ons | ATV, Speed Boat, Romantic Packages - Texoma Resorts</title>
        <meta name="description" content="Add adventure to your stay at Texoma Resorts. ATV rides, speed boat tours, romantic anniversary packages, campfire setups, and stargazing near Lake Texoma." />
      </Helmet>

      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.lakeActivity} alt="Adventure activities at Lake Texoma" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Add-On Experiences</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              Adventures & Experiences
            </h1>
          </motion.div>
        </div>
      </section>

      <section data-testid="experiences-list" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="font-body text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Elevate your stay with unforgettable experiences. Book and pay online instantly
              or submit a request and we will coordinate everything for you.
            </p>
          </motion.div>

          <SpecialsSection onAdd={addExperience} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCES.map((exp, i) => (
              <motion.div key={exp.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
                <ExperienceCard exp={exp} onBook={handleBook} onRequest={handleRequest} onAddToCart={handleAddToCart} loading={loading} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#1A3C34]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="caption-text text-white/50 mb-3">Romantic Escapes</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-white tracking-tight mb-6">
              Anniversary & Celebration Packages
            </h2>
            <p className="font-body text-base text-white/60 max-w-2xl mx-auto mb-8 leading-relaxed">
              Make your special occasion unforgettable. Our romantic packages include room
              decoration, celebration setup, optional flowers, cake, and balloons. Perfect
              for anniversaries, proposals, and couple retreats.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Romantic Getaways", "Anniversary Celebrations", "Proposal Weekends", "Couple Retreats"].map((tag) => (
                <Badge key={tag} variant="outline" className="border-white/20 text-white/70 font-body text-xs">{tag}</Badge>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <RequestDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        exp={selectedExp}
        form={form}
        setForm={setForm}
        loading={formLoading}
        onSubmit={submitRequest}
      />

      <TheaterDialog
        open={theaterOpen}
        onOpenChange={setTheaterOpen}
        exp={theaterExp}
        onConfirm={handleTheaterConfirm}
      />

      <PricingDialog
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        exp={pricingExp}
        onConfirm={handlePricingConfirm}
      />
    </>
  );
}
